/**
 * Tab 2 — Mandate / NSP / External PG "Edit tab" (Task 3, Phase 10A).
 *
 * Loan context: a simple loan-id input + "Load" button (kept deliberately
 * lightweight — this tab's own endpoints are gated on `mandate_pg`, while the
 * active-loans list endpoint is gated on `loan_profile`, so reusing the
 * LoanProfilePage active-loans table here would 403 for a user who only holds
 * mandate_pg access. See views_staff_ops.py:171 vs :1066).
 *
 * Data:  GET /api/lms/staff_ops/mandate/<loan_id>/            (MandateDetailView)
 *        GET /api/lms/staff_ops/loan_amort_mandate/<loan_id>/ (LoanAmortMandateView)
 * Mutations (all require can_edit on `mandate_pg`):
 *        POST .../mandate/<loan_id>/nsp/
 *        POST .../mandate/<loan_id>/fetch_status/       (202, async)
 *        POST .../mandate/<loan_id>/pg_record/          (multipart)
 *        POST .../mandate/<loan_id>/pg_fetch_status/    (always 501 placeholder)
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Search,
    RefreshCcw,
    Save,
    DownloadCloud,
    CreditCard,
    Upload,
    AlertTriangle,
    ShieldCheck,
    ShieldX,
    Ban,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { formatAmount, formatDate, formatDateTime } from './formatters';
import {
    lmsStaffService,
    PresentationType,
    PresentationRow,
    AmortRow,
    PgRecordRow,
    MandateTypeAvailability,
    getLmsApiErrorMessage,
} from './lmsStaffApi';

interface MandateEditPanelProps {
    /** can_edit for the `mandate_pg` tab, resolved by LmsStaffOpsHub. */
    canEdit: boolean;
    /** Optional pre-selected loan (Phase 10B/10C may deep-link into this tab). */
    initialLoanId?: string | null;
}

const READ_ONLY_TOOLTIP = 'You have view-only access to this tab';

/** Wraps a control so a disabled (view-only) state explains itself. */
const EditGate: React.FC<{ canEdit: boolean; children: React.ReactElement }> = ({
    canEdit,
    children,
}) => {
    if (canEdit) return children;
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex cursor-not-allowed">{children}</span>
                </TooltipTrigger>
                <TooltipContent>{READ_ONLY_TOOLTIP}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div>
        <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
        <div className="text-sm font-medium text-gray-800 break-words">{children}</div>
    </div>
);

/** UserMandateRegistrationMapping.auth_type choices ((1,'UPI'),(2,'NACH')) */
const authTypeLabel = (authType: string | null) => {
    if (authType === null || authType === undefined || authType === '') return '-';
    if (String(authType) === '1') return 'UPI (1)';
    if (String(authType) === '2') return 'NACH (2)';
    return String(authType);
};

/** UserMandateRegistrationMapping.status choices (1..5) */
const MANDATE_STATUS_LABELS: Record<string, string> = {
    '1': 'waiting',
    '2': 'processing',
    '3': 'rejected',
    '4': 'accepted',
    '5': 'unknown',
};
const mandateStatusLabel = (status: string | null) => {
    if (status === null || status === undefined || status === '') return '-';
    const label = MANDATE_STATUS_LABELS[String(status)];
    return label ? `${label} (${status})` : String(status);
};

/**
 * Per-type "is this mandate type addable?" row (deliverable B).
 *
 * Business rule: only a MISSING mandate type may be added. When a mandate of
 * this type already exists the Add action is disabled and the reason is stated
 * inline, mirroring the same "explain the disabled control" convention used by
 * AddPresentationPanel's <GatedButton> for its NACH/UPI mandate dependency.
 *
 * This is affordance only — `assert_mandate_type_is_missing()`
 * (backend/lms/mandate_validation.py) independently rejects a duplicate add
 * with a 400 at the real creation entry points
 * (primary_app/api/debitor_api/digio/{upi,nach}_mandate.py), so the guard holds
 * even if this UI is bypassed.
 */
const MandateTypeRow: React.FC<{
    label: PresentationType;
    availability: MandateTypeAvailability | undefined;
    canEdit: boolean;
}> = ({ label, availability, canEdit }) => {
    const exists = availability?.exists ?? false;
    const canAdd = (availability?.can_add ?? false) && canEdit;

    const disabledReason = !canEdit
        ? READ_ONLY_TOOLTIP
        : exists
          ? `A ${label} mandate already exists for this customer — only a missing mandate type can be added.`
          : null;

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
            <div className="space-y-1">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{label}</span>
                    {exists ? (
                        <span className="flex items-center gap-1 text-xs text-green-700">
                            <ShieldCheck className="h-3.5 w-3.5" /> Registered
                        </span>
                    ) : (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                            <ShieldX className="h-3.5 w-3.5" /> Missing
                        </span>
                    )}
                </div>
                {exists && availability?.mandate && (
                    <div className="text-xs text-gray-500">
                        mandate: {availability.mandate.mandate_id || '-'} · UMRN:{' '}
                        {availability.mandate.umrn || '-'} ·{' '}
                        {mandateStatusLabel(availability.mandate.status)}
                    </div>
                )}
            </div>

            {exists ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-400">
                                <Ban className="h-3.5 w-3.5" /> Add {label} Mandate
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{disabledReason}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <span
                    className={`text-xs ${canAdd ? 'text-amber-600' : 'text-gray-400'}`}
                    title={disabledReason ?? undefined}
                >
                    {canAdd
                        ? `${label} mandate can be added — registration is initiated from the customer journey.`
                        : READ_ONLY_TOOLTIP}
                </span>
            )}
        </div>
    );
};

const MandateEditPanel: React.FC<MandateEditPanelProps> = ({ canEdit, initialLoanId }) => {
    const queryClient = useQueryClient();

    const [loanIdInput, setLoanIdInput] = React.useState(initialLoanId || '');
    const [loanId, setLoanId] = React.useState<string | null>(initialLoanId || null);

    const [presentationType, setPresentationType] = React.useState<PresentationType>('UPI');
    const [nspId, setNspId] = React.useState('');
    const [rawResponseText, setRawResponseText] = React.useState('');
    const [photoFile, setPhotoFile] = React.useState<File | null>(null);
    const photoInputRef = React.useRef<HTMLInputElement | null>(null);

    const mandateQueryKey = ['lms-staff-mandate-detail', loanId];
    const amortQueryKey = ['lms-staff-amort-mandate', loanId];

    const {
        data: mandateDetail,
        isLoading: mandateLoading,
        isError: mandateError,
        error: mandateErrorObj,
        refetch: refetchMandate,
    } = useQuery({
        queryKey: mandateQueryKey,
        queryFn: async () => {
            const response = await lmsStaffService.getMandateDetail(loanId as string);
            return response.data;
        },
        enabled: !!loanId,
    });

    const { data: amortData, isLoading: amortLoading } = useQuery({
        queryKey: amortQueryKey,
        queryFn: async () => {
            const response = await lmsStaffService.getLoanAmortMandate(loanId as string);
            return response.data;
        },
        enabled: !!loanId,
    });

    const invalidateMandate = () => {
        queryClient.invalidateQueries({ queryKey: mandateQueryKey });
        queryClient.invalidateQueries({ queryKey: amortQueryKey });
    };

    const saveNspMutation = useMutation({
        mutationFn: () =>
            lmsStaffService.updateNspId(loanId as string, {
                presentation_type: presentationType,
                nsp_id: nspId.trim(),
            }),
        onSuccess: (response) => {
            toast.success(
                `NSP ID saved for ${response.data.presentation_type} presentation #${response.data.id}`
            );
            invalidateMandate();
        },
        onError: (error: unknown) => {
            toast.error(getLmsApiErrorMessage(error, 'Failed to save NSP ID'));
        },
    });

    const fetchStatusMutation = useMutation({
        mutationFn: (presentationId: number) =>
            lmsStaffService.fetchPresentationStatus(loanId as string, presentationId),
        onSuccess: () => {
            toast.success('Status fetch dispatched, refresh shortly');
            // The backend returns 202 and completes the Digio poll in a Celery
            // worker, so give it a moment before re-reading the row.
            setTimeout(invalidateMandate, 5000);
        },
        onError: (error: unknown) => {
            toast.error(getLmsApiErrorMessage(error, 'Failed to dispatch status fetch'));
        },
    });

    const savePgRecordMutation = useMutation({
        mutationFn: () => {
            const formData = new FormData();
            formData.append('raw_response_text', rawResponseText);
            if (photoFile) formData.append('photo', photoFile);
            return lmsStaffService.createPgRecord(loanId as string, formData);
        },
        onSuccess: () => {
            toast.success('PG record saved');
            setRawResponseText('');
            setPhotoFile(null);
            if (photoInputRef.current) photoInputRef.current.value = '';
            invalidateMandate();
        },
        onError: (error: unknown) => {
            toast.error(getLmsApiErrorMessage(error, 'Failed to save PG record'));
        },
    });

    const pgFetchStatusMutation = useMutation({
        mutationFn: () => lmsStaffService.pgFetchStatus(loanId as string),
        onSuccess: (response) => {
            // Should never happen today (the view always returns 501) but
            // surface whatever came back rather than silently swallowing it.
            toast.info(JSON.stringify(response.data));
        },
        onError: (error: unknown) => {
            // Always a 501 with the explanatory `error` string — surfaced
            // verbatim so it reads as a known limitation, not a bug.
            toast.error(
                getLmsApiErrorMessage(error, 'Automated PG status fetch is not available.')
            );
        },
    });

    const handleLoad = () => {
        const trimmed = loanIdInput.trim();
        if (!trimmed) {
            toast.error('Enter a loan ID to load');
            return;
        }
        setLoanId(trimmed);
    };

    const mandate = mandateDetail?.mandate ?? amortData?.mandate ?? null;
    const presentations: PresentationRow[] = mandateDetail?.presentations || [];
    const pgRecords: PgRecordRow[] = mandateDetail?.pg_records || [];
    const amortSchedule: AmortRow[] = amortData?.amort_schedule || [];

    return (
        <div className="space-y-6">
            {/* ── Loan selector ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="mandate-loan-id">Loan ID</Label>
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                id="mandate-loan-id"
                                placeholder="e.g. IL0000123"
                                value={loanIdInput}
                                onChange={(e) => setLoanIdInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleLoad();
                                }}
                                className="pl-9"
                            />
                        </div>
                    </div>
                    <Button type="button" onClick={handleLoad}>
                        Load
                    </Button>
                    {loanId && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => refetchMandate()}
                            className="gap-2"
                        >
                            <RefreshCcw className="h-4 w-4" /> Refresh
                        </Button>
                    )}
                    {!canEdit && (
                        <span className="text-xs text-amber-600">
                            View-only access — editing controls are disabled.
                        </span>
                    )}
                </div>
            </div>

            {!loanId && (
                <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
                    Enter a loan ID above to load its mandate, presentations and PG records.
                </div>
            )}

            {loanId && mandateLoading && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-24 w-full" />
                </div>
            )}

            {loanId && mandateError && (
                <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600 border border-red-100">
                    {getLmsApiErrorMessage(
                        mandateErrorObj,
                        'Unable to load mandate details for this loan.'
                    )}
                </div>
            )}

            {loanId && !mandateLoading && !mandateError && (
                <>
                    {/* ── Mandate info ──────────────────────────────────── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">Resolved Mandate</h3>
                        {mandate ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <Field label="Mandate ID">{mandate.mandate_id || '-'}</Field>
                                <Field label="UMRN">{mandate.umrn || '-'}</Field>
                                <Field label="Auth Type">{authTypeLabel(mandate.auth_type)}</Field>
                                <Field label="Status">{mandateStatusLabel(mandate.status)}</Field>
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                No mandate could be resolved for this loan.
                            </div>
                        )}
                    </div>

                    {/* ── Mandate availability per type (deliverable B) ─── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">
                            Mandate Availability
                        </h3>
                        <p className="text-xs text-gray-500">
                            Only a missing mandate type may be added. A type that is
                            already registered cannot be added again — the backend
                            rejects any such attempt.
                        </p>
                        <div className="space-y-2">
                            <MandateTypeRow
                                label="UPI"
                                availability={mandateDetail?.mandate_availability?.upi}
                                canEdit={canEdit}
                            />
                            <MandateTypeRow
                                label="NACH"
                                availability={mandateDetail?.mandate_availability?.nach}
                                canEdit={canEdit}
                            />
                        </div>
                    </div>

                    {/* ── UPI/NACH toggle + NSP ID ──────────────────────── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <h3 className="text-sm font-semibold text-gray-800">
                            Presentation Type &amp; NSP ID
                        </h3>

                        <RadioGroup
                            value={presentationType}
                            onValueChange={(value) =>
                                setPresentationType(value as PresentationType)
                            }
                            className="flex items-center gap-6"
                        >
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="UPI" id="pt-upi" />
                                <Label htmlFor="pt-upi" className="cursor-pointer">
                                    UPI
                                </Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <RadioGroupItem value="NACH" id="pt-nach" />
                                <Label htmlFor="pt-nach" className="cursor-pointer">
                                    NACH
                                </Label>
                            </div>
                        </RadioGroup>

                        <div className="flex flex-wrap items-end gap-3">
                            <div className="space-y-1">
                                <Label htmlFor="nsp-id">NSP ID</Label>
                                <Input
                                    id="nsp-id"
                                    placeholder="Digio presentation / plan id"
                                    value={nspId}
                                    onChange={(e) => setNspId(e.target.value)}
                                    disabled={!canEdit}
                                    className="w-full sm:w-80"
                                />
                            </div>
                            <EditGate canEdit={canEdit}>
                                <Button
                                    type="button"
                                    onClick={() => saveNspMutation.mutate()}
                                    disabled={
                                        !canEdit || !nspId.trim() || saveNspMutation.isPending
                                    }
                                    className="gap-2"
                                >
                                    <Save className="h-4 w-4" /> Save NSP ID
                                </Button>
                            </EditGate>
                        </div>
                    </div>

                    {/* ── Presentations ─────────────────────────────────── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">Presentations</h3>
                        {presentations.length === 0 ? (
                            <div className="text-sm text-gray-500">
                                No presentation requests recorded for this loan.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                                        <tr>
                                            <th className="px-3 py-2">ID</th>
                                            <th className="px-3 py-2">Type</th>
                                            <th className="px-3 py-2">NSP ID</th>
                                            <th className="px-3 py-2">Settlement</th>
                                            <th className="px-3 py-2">Status</th>
                                            <th className="px-3 py-2">Last Polled</th>
                                            <th className="px-3 py-2">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {presentations.map((p) => (
                                            <tr key={p.id} className="border-t border-gray-100">
                                                <td className="px-3 py-2">{p.id}</td>
                                                <td className="px-3 py-2">
                                                    {p.presentation_type || '-'}
                                                </td>
                                                <td className="px-3 py-2 break-all">
                                                    {p.nsp_id || '-'}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatDateTime(p.settlement_datetime)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {p.final_status || '-'}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatDateTime(p.last_polled_at)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <EditGate canEdit={canEdit}>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="gap-2"
                                                            disabled={
                                                                !canEdit ||
                                                                !p.nsp_id ||
                                                                fetchStatusMutation.isPending
                                                            }
                                                            onClick={() =>
                                                                fetchStatusMutation.mutate(p.id)
                                                            }
                                                        >
                                                            <DownloadCloud className="h-3.5 w-3.5" />{' '}
                                                            Fetch Status
                                                        </Button>
                                                    </EditGate>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── External PG ───────────────────────────────────── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <CreditCard className="h-4 w-4 text-gray-400" /> External Payment
                            Gateway
                        </h3>

                        <div className="space-y-2">
                            <Label htmlFor="pg-raw">Paste PG response (JSON or plain text)</Label>
                            <Textarea
                                id="pg-raw"
                                rows={6}
                                placeholder='{"status": "SUCCESS", ...}'
                                value={rawResponseText}
                                onChange={(e) => setRawResponseText(e.target.value)}
                                disabled={!canEdit}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="pg-photo">Attach photo (optional)</Label>
                            <Input
                                id="pg-photo"
                                type="file"
                                accept="image/*"
                                ref={photoInputRef}
                                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                                disabled={!canEdit}
                            />
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <EditGate canEdit={canEdit}>
                                <Button
                                    type="button"
                                    onClick={() => savePgRecordMutation.mutate()}
                                    disabled={
                                        !canEdit ||
                                        !rawResponseText.trim() ||
                                        savePgRecordMutation.isPending
                                    }
                                    className="gap-2"
                                >
                                    <Upload className="h-4 w-4" /> Save PG Record
                                </Button>
                            </EditGate>
                            <EditGate canEdit={canEdit}>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => pgFetchStatusMutation.mutate()}
                                    disabled={!canEdit || pgFetchStatusMutation.isPending}
                                    className="gap-2"
                                >
                                    <AlertTriangle className="h-4 w-4" /> Fetch via PG API
                                </Button>
                            </EditGate>
                        </div>
                        <p className="text-xs text-gray-500">
                            No payment gateway is configured for automated status fetch yet — the
                            "Fetch via PG API" button will report that limitation. Use the manual
                            paste above in the meantime.
                        </p>

                        {/* Existing PG records — inline info only, no downloads */}
                        <div className="space-y-2 border-t pt-4">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Existing PG Records
                            </h4>
                            {pgRecords.length === 0 ? (
                                <div className="text-sm text-gray-500">
                                    No external PG records saved for this loan.
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-100 text-sm">
                                    {pgRecords.map((r) => (
                                        <li
                                            key={r.id}
                                            className="flex flex-wrap items-center gap-x-6 gap-y-1 py-2"
                                        >
                                            <span className="text-gray-800">#{r.id}</span>
                                            <span className="text-gray-600">
                                                source: {r.source || '-'}
                                            </span>
                                            <span className="text-gray-600">
                                                {formatDateTime(r.created_at)}
                                            </span>
                                            <span className="text-gray-600">
                                                photo: {r.has_photo ? 'yes' : 'no'}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* ── Amort schedule ────────────────────────────────── */}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">
                            Amortisation Schedule
                        </h3>
                        {amortLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : amortSchedule.length === 0 ? (
                            <div className="text-sm text-gray-500">
                                No amortisation rows found for this loan.
                            </div>
                        ) : (
                            <div className="max-h-80 overflow-auto">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                                        <tr>
                                            <th className="px-3 py-2">Series</th>
                                            <th className="px-3 py-2">Payment Date</th>
                                            <th className="px-3 py-2">Amount</th>
                                            <th className="px-3 py-2">Principal</th>
                                            <th className="px-3 py-2">Interest</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {amortSchedule.map((row, index) => (
                                            <tr
                                                key={`${row.loan_series ?? 'row'}-${index}`}
                                                className="border-t border-gray-100"
                                            >
                                                <td className="px-3 py-2">
                                                    {row.loan_series ?? '-'}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatDate(row.payment_date)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatAmount(row.payment_amount)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatAmount(row.payment_amount_p)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {formatAmount(row.payment_amount_i)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default MandateEditPanel;